import { useMemo } from "react";
import { SiteId } from "@visioni/a3s-schema";
import { useSites } from "@src/features/cameras/hooks";
import { useCameras } from "@src/services/api/hooks";
import { formatCameraLabel, getCameraKeyString } from "@src/utils/key-parser";

export interface SiteOption {
  readonly id: string;
  readonly label: string;
}

export interface CameraOption {
  readonly key: string;
  readonly siteId: string;
  readonly label: string;
}

export interface ActivationScopeOptions {
  readonly sites: readonly SiteOption[];
  readonly cameras: readonly CameraOption[];
  readonly isLoading: boolean;
  /** Display name for a site id, falling back to the id for a site this tenant no longer lists. */
  readonly siteLabel: (siteId: string) => string;
  /** Display name for a camera key, falling back to the key itself. */
  readonly cameraLabel: (cameraKey: string) => string;
}

const byLabel = (a: { label: string }, b: { label: string }) =>
  a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" });

/**
 * The tenant's sites and cameras, as options for the activation filters and as
 * names for the ids an activation carries.
 *
 * From the app's own existing site and camera sources rather than anything
 * Patriot-side: the replica knows only the ids its marker recorded.
 */
export function useActivationScopeOptions(): ActivationScopeOptions {
  const { sites, isLoading: sitesLoading } = useSites();
  const { data: cameras, isLoading: camerasLoading } = useCameras();

  const siteOptions = useMemo(
    () =>
      (sites ?? [])
        .map((s) => ({ id: SiteId.toString(s.siteId), label: s.siteName || SiteId.toString(s.siteId) }))
        .sort(byLabel),
    [sites],
  );

  const cameraOptions = useMemo(
    () =>
      (cameras ?? [])
        .map((c) => {
          const key = getCameraKeyString(c);
          return { key, siteId: c.site.id, label: c.nickname || formatCameraLabel(key) || key };
        })
        .sort(byLabel),
    [cameras],
  );

  const siteNames = useMemo(() => new Map(siteOptions.map((s) => [s.id, s.label])), [siteOptions]);
  const cameraNames = useMemo(() => new Map(cameraOptions.map((c) => [c.key, c.label])), [cameraOptions]);

  return {
    sites: siteOptions,
    cameras: cameraOptions,
    isLoading: sitesLoading || camerasLoading,
    siteLabel: (siteId) => siteNames.get(siteId) ?? siteId,
    cameraLabel: (cameraKey) => cameraNames.get(cameraKey) ?? cameraKey,
  };
}
